import { z } from "zod";
import { optionalIco } from "../lib/zod-helpers";
import { paginationSchema, sortSchema } from "./pagination";

/**
 * HCA Moravia — validační schémata pro `hca.firmy` tRPC router.
 */

/**
 * Inline založení firmy z jiného formuláře (quickAdd přihlášky, nový kontakt).
 * Užší než `createFirmaSchema` — bez poznámky a správce; router firmu dedupuje
 * podle IČO a normalizovaného názvu, takže duplicitní zadání firmu nezaloží.
 */
export const novaFirmaInlineSchema = z.object({
  nazev: z
    .string()
    .trim()
    .min(1, "Název firmy je povinný")
    .max(255, "Název musí být 255 znaků nebo méně"),
  ico: optionalIco(),
  dic: z.string().trim().max(14, "DIČ musí být 14 znaků nebo méně").optional(),
  adresa: z.string().trim().max(500, "Adresa musí být 500 znaků nebo méně").optional(),
});

export type NovaFirmaInlineInput = z.infer<typeof novaFirmaInlineSchema>;

/** Vytvoření firmy. `ico` je volitelné; unikátnost řeší router (CONFLICT). */
export const createFirmaSchema = z.object({
  nazev: z.string().min(1, "Název firmy je povinný").max(255, "Název musí být 255 znaků nebo méně"),
  ico: optionalIco(),
  dic: z.string().max(15, "DIČ musí být 15 znaků nebo méně").optional(),
  adresa: z.string().max(1000, "Adresa musí být 1000 znaků nebo méně").optional(),
  poznamka: z.string().max(2000, "Poznámka musí být 2000 znaků nebo méně").optional(),
  spravceId: z.string().max(255, "ID správce musí být 255 znaků nebo méně").optional(),
});

/** Partial update + povinné id. */
export const updateFirmaSchema = createFirmaSchema.partial().extend({
  id: z.string().uuid("Neplatné ID firmy"),
});

/** Whitelist řazení firem — router mapuje klíče na sloupce. */
export const FIRMY_SORT_KEYS = ["nazev", "ico", "vytvoreno"] as const;
export type FirmySortKey = (typeof FIRMY_SORT_KEYS)[number];

/** List firem — paginace + vyhledávání + filter na ico. */
export const listFirmySchema = paginationSchema.merge(sortSchema(FIRMY_SORT_KEYS)).extend({
  search: z.string().max(255, "Hledaný text musí být 255 znaků nebo méně").optional(),
  ico: z.string().max(8, "IČO musí být max 8 znaků").optional(),
  archivovane: z.coerce.boolean().optional(),
  /** Filtr na přiřazeného obchodníka (spravceId): `ma` = má, `nema` = nemá. */
  obchodnik: z.enum(["ma", "nema"]).optional(),
  /**
   * Režim pro našeptávač: přeskočí `count(*)` přes celou tabulku, který
   * napovídání jen zdržuje. Odpověď pak má `total: -1` — kdo potřebuje
   * stránkování, flag neposílá.
   */
  lightweight: z.boolean().optional(),
});

/** ID-only schema pro getById / archive / restore. */
export const firmaIdSchema = z.object({
  id: z.string().uuid("Neplatné ID firmy"),
});

/** Přiřazení správce. */
export const assignSpravceSchema = z.object({
  firmaId: z.string().uuid("Neplatné ID firmy"),
  spravceId: z.string().max(255, "ID správce musí být 255 znaků nebo méně").nullable(),
});

export type CreateFirmaInput = z.infer<typeof createFirmaSchema>;
export type UpdateFirmaInput = z.infer<typeof updateFirmaSchema>;
export type ListFirmyInput = z.infer<typeof listFirmySchema>;
export type FirmaIdInput = z.infer<typeof firmaIdSchema>;
export type AssignSpravceInput = z.infer<typeof assignSpravceSchema>;
